import { useEffect, useState } from "react";
import { Checkbox, DialogContent, FormControlLabel, Grid, TextField } from "@mui/material";
import { ContactMail } from "@mui/icons-material";
import { toast } from "react-toastify";

import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	MolePrimaryButton,
	useMoleThemeColor,
} from "../../components/MoleDialogShell";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { ContactBlock } from "../../util/Batch";
import { MYSQL_LIMITS } from "../../util/constants";
import { SavedAddress, contactFromSaved, emptyContact, normalizeContact } from "./contactBlock";

interface Props {
	open: boolean;
	onClose: () => void;
	clientId: number;
	/** Existing entry to edit; omit to add a new one. */
	entry?: SavedAddress | null;
	onSaved: () => void;
}

/** Add or edit one entry in a client's address book. */
const AddressEntryDialog = ({ open, onClose, clientId, entry, onSaved }: Props) => {
	const { api } = useAuth();
	const { t, queueReRun } = useUI();
	const themeColor = useMoleThemeColor();

	const [form, setForm] = useState<ContactBlock>(emptyContact());
	const [makeDefault, setMakeDefault] = useState(false);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!open) return;
		setForm(entry ? { ...emptyContact(), ...contactFromSaved(entry) } : emptyContact());
		setMakeDefault(Boolean(entry?.is_default));
		setSaving(false);
	}, [open, entry]);

	const patch = (field: keyof ContactBlock, value: string) =>
		setForm((prev) => ({ ...prev, [field]: value }));

	const canSave = Boolean(normalizeContact(form)) && !saving;

	const handleSave = async () => {
		if (!canSave) return;
		setSaving(true);
		const body = { ...normalizeContact(form), make_default: makeDefault };
		const res = entry
			? await api.put(`/clients/${clientId}/addresses/${entry.id}`, body)
			: await api.post(`/clients/${clientId}/addresses`, body);
		setSaving(false);

		if (!res) return;
		if (res.status === 200 || res.status === 201 || res.status === 204) {
			toast.success(t("common:toast.success.addressSaved"), { autoClose: 3000 });
			onSaved();
			onClose();
			return;
		}
		if (res.status === 401 || res.status === 422) {
			queueReRun(handleSave);
			return;
		}
		toast.error(res.data?.message || t("common:toast.error.integrity"), { autoClose: 3000 });
	};

	const field = (
		name: keyof ContactBlock,
		labelKey: string,
		maxLength: number,
		extra?: { multiline?: boolean; size?: number },
	) => (
		<Grid size={{ xs: 12, sm: extra?.size ?? 6 }}>
			<TextField
				fullWidth
				size="small"
				variant="filled"
				label={t(labelKey)}
				value={String(form[name] ?? "")}
				inputProps={{ maxLength }}
				multiline={extra?.multiline}
				rows={extra?.multiline ? 2 : undefined}
				onChange={(e) => patch(name, e.target.value)}
			/>
		</Grid>
	);

	return (
		<MoleDialog open={open} onClose={onClose} maxWidth="sm" fullWidth themeColor={themeColor}>
			<MoleDialogHeader
				title={t(entry ? "common:addressBook.editEntry" : "common:addressBook.addEntry")}
				icon={<ContactMail />}
				onClose={onClose}
				themeColor={themeColor}
			/>
			<DialogContent sx={{ pt: 2 }}>
				<Grid container spacing={2}>
					{field("label", "common:addressLine.label", MYSQL_LIMITS.VARCHAR(50).maxLength)}
					{field("con_name", "common:addressLine.name", MYSQL_LIMITS.VARCHAR(100).maxLength)}
					{field("con_phone", "common:addressLine.phone", MYSQL_LIMITS.VARCHAR(50).maxLength)}
					{field("con_email", "common:addressLine.email", MYSQL_LIMITS.VARCHAR(100).maxLength)}
					{field("con_address", "common:addressLine.address", MYSQL_LIMITS.VARCHAR(255).maxLength, {
						multiline: true,
						size: 12,
					})}
					{field("con_details", "common:addressLine.details", MYSQL_LIMITS.TEXT.maxLength, {
						multiline: true,
						size: 12,
					})}
					<Grid size={{ xs: 12 }}>
						<FormControlLabel
							control={
								<Checkbox checked={makeDefault} onChange={(e) => setMakeDefault(e.target.checked)} />
							}
							label={t("common:addressLine.makeDefault")}
						/>
					</Grid>
				</Grid>
			</DialogContent>
			<MoleDialogActions>
				<MoleCancelButton onClick={onClose}>{t("common:ui.buttons.cancel")}</MoleCancelButton>
				<MolePrimaryButton onClick={handleSave} disabled={!canSave}>
					{t("common:ui.buttons.save")}
				</MolePrimaryButton>
			</MoleDialogActions>
		</MoleDialog>
	);
};

export default AddressEntryDialog;
